import React, { useState, useEffect, useCallback } from "react";
import ProfileImageWithFullscreen from "./profile-image-with-fullscreen";
import { useToast } from "../../Utils/toastContext";
import { debounce, reject } from "lodash";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import DocumentPreview from "../../Pages/Create-Investment/documentpreview";
import CommentInput from "./inputfield-withemojis";
import {
  UpdateFieldStatusTrue,
  UpdateFieldStatusFalse,
} from "../../Redux/store/userSlice";

const ReviewPopup = ({
  isOpen,
  onClose,
  currentField,
  currentFieldName,
  currentFieldValue,
  currentFieldType,
}) => {
  const { showToast } = useToast();
  const BASE_URL = process.env.REACT_APP_BASE_URL;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState("");
  const [activeTab, setActiveTab] = useState("comments");
  const [rejectReason, setRejectReason] = useState(null);
  const [comment, setComment] = useState("");
  const [currentDateTime, setCurrentDateTime] = useState("");
  const [loading, setLoading] = useState(false);
  const [currentDocument, setcurrentDocument] = useState({});
  const [rejectedBy, setRejectedBy] = useState("");
  const [rejectedByRole, setRejectedByRole] = useState("");
  const [rejectionTime, setRejectionTime] = useState("");
  const [rejectionDate, setRejectionDate] = useState("");

  const loggedInUserRole = useSelector((state) => state.user.roleName);
  const fileIdsRedux = useSelector((state) => state.user.fileIdsRedux);

  const handleFileClick = (docData) => {
    setcurrentDocument(docData);
    setIsModalOpen(true);
  };
  const [comments, setComments] = useState([]);
  const formatDateTime = (dateString) => {
    const [datePart, timePart, meridian, timezone] = dateString.split(" ");
    const [day, month, year] = datePart.split("/");
    const [hours, minutes] = timePart.split(":");

    // Convert 24-hour format to 12-hour format
    const hours12 = (+hours % 12 || 12).toString();
    const formattedTime = `${hours12}:${minutes}${meridian}`;
    return `${day}/${month}/${year} | ${formattedTime}`;
  };
  const addComment = async (action) => {
    const token = localStorage.getItem("jwtToken");
    const headers = {
      "Content-Type": "application/json",
    };

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    const apiUrl = `${BASE_URL}/v1/onboarding/verifications/comment`;
    const bodyData = {
      verification_id: currentField.verification_id,
      comment: comment,
      file_ids: fileIdsRedux,
    };
    try {
      const response = await fetch(apiUrl, {
        method: "POST",
        headers: headers,
        body: JSON.stringify(bodyData),
      });

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem("jwtToken");
          navigate("/login");
          throw new Error("Session expired. Please log in again.");
        }
        const errorData = await response.json().catch(() => ({
          message: "An unexpected error occurred",
        }));

        throw new Error(errorData.message || "Something went wrong");
      }

      return await response.json();
    } catch (error) {
      throw new Error(error.message || "Network error, please try again later");
    }
  };
  const handlSendComment = useCallback(
    debounce(async (e, action) => {
      e.preventDefault();
      let toastMessage = null;
      try {
        dispatch(UpdateFieldStatusFalse());
        const response = await addComment(action);
        if (response.status == 200) {
          toastMessage = {
            message:
              action == "approve"
                ? "Approval completed successfully"
                : action == "comment"
                ? "Comment added successfully"
                : "Request rejected successfully",
            type: "success",
          };
          dispatch(UpdateFieldStatusTrue());
          onClose();
        } else {
          toastMessage = { message: response.message, type: "error" };
        }
      } catch (error) {
        setSelectedOption("");
        toastMessage = { message: error.message, type: "error" };
      } finally {
        setLoading(false); // Ensure `loading` is false first
        // Show toast after loading is false
        if (toastMessage) {
          setTimeout(() => {
            showToast(toastMessage.message, toastMessage.type);
          }, 1000); // Small delay if needed
        }
      }
    }, 1000),
    [currentField, comment, rejectReason, selectedOption, fileIdsRedux] // Dependencies
  );
  const cleanDateTime = (input) => {
    // Remove timezone like AEDT, GMT, UTC, etc.
    const cleaned = input.replace(/\s[A-Z]{2,5}$/, "").trim();
    const [date, time, meridian] = cleaned.split(" ");
    return {
      date, // "05/11/2025"
      time: `${time} ${meridian}`, // "10:12 AM"
    };
  };
  useEffect(() => {
    const updateDateTime = () => {
      const now = new Date();
      const formatted = now
        .toLocaleString("en-GB", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        })
        .replace(",", " |")
        .toUpperCase(); // 🔥 ensures AM/PM is capital

      setCurrentDateTime(formatted);
    };

    updateDateTime(); // set immediately
    const interval = setInterval(updateDateTime, 60000); // update every minute

    return () => clearInterval(interval);
  }, []);
  useEffect(() => {
    setComments(currentField.comments);
    if (currentField.status == "approved") {
      setSelectedOption("approve");
    }
    if (currentField.status == "rejected") {
      setSelectedOption("reject");
      setRejectReason(currentField.rejection_comment);
      const { date, time } = cleanDateTime(currentField.rejection_timestamp);
      setRejectionDate(date);
      setRejectionTime(time);
      setRejectedBy(
        currentField.rejected_by.first_name +
          " " +
          currentField.rejected_by.last_name
      );
      setRejectedByRole(currentField.rejected_by.role);
    }
  }, [currentField]);
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
      {loading && (
        <div className="fixed top-0 left-0 w-full h-full flex justify-center items-center bg-black bg-opacity-50 z-[999]">
          <div className="w-12 h-12 border-4 border-white border-t-transparent rounded-full animate-spin"></div>
        </div>
      )}
      {/* Modal Container */}
      <div className="bg-white h-[90vh] w-[90vw] max-w-md md:max-w-lg rounded-lg shadow-lg overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between bg-[#F2F2F2] py-2 px-3 text-white">
          <h2 className="text-sm text-black">All Comments</h2>
          <button onClick={onClose}>
            <img src="/add.svg" alt="Close" className="w-10 h-10" />{" "}
          </button>
        </div>

        {/* User Details */}
        <div className="p-4 bg-[#3047EC] shadow-md">
          <div className="flex justify-between items-center">
            {/* Left Section */}
            <div>
              <h3 className="text-sm text-[#A9E5FF] tracking-wide font-medium">
                {currentFieldName}
              </h3>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-xs text-white opacity-80">Status:</span>
                <span
                  className={`px-2 py-0.5 text-xs font-semibold rounded-full ${
                    currentField.status === "approved"
                      ? "bg-green-200 text-green-800"
                      : currentField.status === "rejected"
                      ? "bg-red-200 text-red-800"
                      : "bg-yellow-200 text-yellow-800"
                  }`}
                >
                  {currentField.status
                    ? currentField.status.charAt(0).toUpperCase() +
                      currentField.status.slice(1)
                    : ""}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Comments Section */}
        <div className="p-4 bg-white">
          <div className="">
            <div className="flex border-b border-gray-200">
              {/* Comments Tab */}
              <button
                onClick={() => setActiveTab("comments")}
                className={`flex-1 text-sm font-semibold pb-2 ${
                  activeTab === "comments"
                    ? "text-black border-b-2 border-black"
                    : "text-gray-500 hover:text-black"
                }`}
              >
                Comments ({currentField.comments.length})
              </button>

              {/* Reject Reason Tab */}
              {selectedOption == "reject" && (
                <button
                  onClick={() => setActiveTab("reject")}
                  className={`flex-1 text-sm font-semibold pb-2 ${
                    activeTab === "reject"
                      ? "text-black border-b-2 border-black"
                      : "text-gray-500 hover:text-black"
                  }`}
                >
                  Reject Reason
                </button>
              )}
            </div>

            {/* Tab Content */}
            <div className="mt-3">
              {activeTab === "comments" && (
                <div className="flex flex-col h-[80vh] max-h-[600px] bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden">
                  {/* Scrollable Comments Section */}
                  <div className="flex-1 overflow-y-auto space-y-4 p-4 sm:p-5">
                    {comments.map((comment, index) => {
                      const isCurrentUser =
                        comment.added_by?.role === loggedInUserRole;

                      return (
                        <div
                          key={index}
                          className={`flex ${
                            isCurrentUser ? "justify-end" : "justify-start"
                          }`}
                        >
                          <div
                            className={`flex items-start gap-3 px-4 py-3 rounded-2xl max-w-[80%] sm:max-w-[70%] shadow-sm ${
                              isCurrentUser ? "bg-[#E7EDFF]" : "bg-[#F5F5F5]"
                            }`}
                          >
                            {/* Avatar */}
                            <img
                              src={
                                comment.added_by?.profile_picture ||
                                "/avatar.svg"
                              }
                              alt={comment.added_by?.first_name}
                              className="w-8 h-8 sm:w-10 sm:h-10 rounded-full object-cover border border-gray-200"
                            />

                            {/* Message Content */}
                            <div className="flex-1 relative">
                              <div className="flex justify-between items-center mb-1">
                                <h4 className="text-xs sm:text-sm font-semibold text-black">
                                  <span>
                                    {isCurrentUser
                                      ? "Me"
                                      : `${comment.added_by?.first_name} ${comment.added_by?.last_name}`}
                                  </span>
                                  <span className="text-[#3047EC] text-xs ml-1">
                                    {comment.added_by.role === "super_admin"
                                      ? "Admin"
                                      : comment.added_by.role === "customer"
                                      ? "Customer"
                                      : "Staff"}
                                  </span>
                                </h4>
                              </div>

                              <p className="text-gray-800 text-xs sm:text-sm break-words">
                                {comment.comment}
                              </p>
                              {comment.files.length > 0 && (
                                <>
                                  {comment.files.map((file, index) => (
                                    <div
                                      onClick={(e) => {
                                        e.preventDefault();
                                        handleFileClick(file);
                                      }}
                                      className="w-40 cursor-pointer mt-2 bg-white border shadow-md rounded-lg flex items-center justify-between px-3 py-2"
                                    >
                                      <div className="flex-col flex">
                                        <span className="text-black text-xs underline">
                                          {file.file_name}
                                        </span>
                                      </div>
                                    </div>
                                  ))}
                                </>
                              )}
                              <div
                                className={`mt-1 text-[10px] sm:text-xs ${
                                  isCurrentUser
                                    ? "text-right text-gray-400"
                                    : "text-left text-gray-400"
                                }`}
                              >
                                {formatDateTime(comment.timestamp)}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Fixed Comment Input */}
                  <div className="mt-auto">
                    <CommentInput
                      comment={comment}
                      setComment={setComment}
                      handlSendComment={handlSendComment}
                      setLoading={setLoading}
                    />
                  </div>
                </div>
              )}
              {activeTab === "reject" && (
                <div className="space-y-4 mt-3 h-full">
                  <div className="p-4 flex flex-col">
                    <div className="w-full">
                      <textarea
                        id="reject-reason"
                        rows="3"
                        value={rejectReason ? rejectReason : comment}
                        disabled={rejectReason != null}
                        onChange={(e) => setComment(e.target.value)}
                        className="w-full mt-2 p-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-black"
                        placeholder="Add reason here..."
                      />

                      {/* Date + Buttons row */}
                      <div className="font-medium flex flex-col md:flex-row justify-between items-center mt-6 space-y-2 md:space-y-0">
                        {/* DateTime on the left */}
                        {rejectReason == null && (
                          <span className="text-sm text-[#9C9C9C]">
                            {currentDateTime}{" "}
                          </span>
                        )}
                        {rejectReason != null && (
                          <span className="text-sm text-gray-600">
                            Rejected by{" "}
                            <span className="font-semibold text-black">
                              {rejectedBy}
                            </span>{" "}
                            <span className="text-[#3047EC]">
                              (
                              {rejectedByRole === "super_admin"
                                ? "Super Admin"
                                : rejectedByRole}
                              )
                            </span>{" "}
                            on{" "}
                            <span className="text-[#3047EC] font-medium">
                              {rejectionDate}{" "}
                            </span>
                            at{" "}
                            <span className="text-[#3047EC] font-medium">
                              {rejectionTime}
                            </span>
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {isModalOpen && currentDocument && (
        <DocumentPreview
          fileUrl={currentDocument.presigned_url}
          fileName={currentDocument.file_name}
          onClose={() => setIsModalOpen(false)}
        />
      )}
    </div>
  );
};
export default ReviewPopup;
